
import { Link } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { hasAnyRole, pathSegunRoles } from "../utils/roleRedirect";

export default function Dashboard() {
  const { user: authUser, logout } = useAuth();

  const nombre = authUser?.nombres ?? "Cargando";
  const roles: Array<{ slug: string; nombre: string }> = authUser?.roles ?? [];
  const initials = (() => {
    const partes = `${authUser?.nombres ?? ""} ${authUser?.apellidos ?? ""}`
      .trim()
      .split(/\s+/);
    return partes
      .slice(0, 2)
      .map((p) => p[0]?.toUpperCase() ?? "")
      .join("");
  })();

  // Roles normalizados
  const puedeAdmin = hasAnyRole(authUser, "ADMIN", "ADMINISTRADOR");
  const puedeEval = hasAnyRole(
    authUser,
    "RESPONSABLE",
    "RESPONSABLE_ACADEMICO",
    "EVALUADOR"
  );
  const puedeCom = hasAnyRole(authUser, "COMUNICACIONES");

  // Para "Evaluaciones" priorizamos su módulo específico
  const evaluadorPath =
    hasAnyRole(authUser, "RESPONSABLE", "RESPONSABLE_ACADEMICO")
      ? "/responsable/panel"
      : hasAnyRole(authUser, "EVALUADOR")
      ? "/evaluador/panel"
      : pathSegunRoles(authUser) ?? "/dashboard";

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-cyan-950">
      {/* Topbar */}
      <header className="sticky top-0 z-20 border-b border-white/10 bg-slate-900/60 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3 md:px-6">
          <div className="flex items-center gap-3">
            <div className="grid h-9 w-9 place-items-center rounded-xl bg-cyan-500/20">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M12 3l7 4v6c0 4-3 7-7 8-4-1-7-4-7-8V7l7-4z" stroke="#22d3ee" strokeWidth="1.5" />
                <path d="M9 12h6" stroke="#22d3ee" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </div>
            <div>
              <h1 className="text-base font-bold text-white">OH SanSi</h1>
              <p className="text-xs text-slate-400">Evaluación y Clasificación</p>
            </div>
          </div>

          <div className="flex items-center gap-4">
            {puedeAdmin && (
              <nav className="hidden items-center gap-2 md:flex">
                <Link
                  to="/admin/responsables"
                  className="rounded-lg border border-white/10 bg-slate-800/60 px-3 py-1.5 text-xs font-medium text-slate-200 hover:border-cyan-400/40"
                >
                  Responsables
                </Link>
                <Link
                  to="/admin/usuarios"
                  className="rounded-lg border border-white/10 bg-slate-800/60 px-3 py-1.5 text-xs font-medium text-slate-200 hover:border-cyan-400/40"
                >
                  Usuarios
                </Link>
                <Link
                  to="/admin/auditoria"
                  className="rounded-lg border border-white/10 bg-slate-800/60 px-3 py-1.5 text-xs font-medium text-slate-200 hover:border-cyan-400/40"
                >
                  Auditoría
                </Link>
              </nav>
            )}

            <div className="hidden gap-2 md:flex">
              {roles.length > 0 ? (
                roles.map((r) => (
                  <span
                    key={r.slug}
                    className="rounded-full border border-white/10 bg-slate-800/60 px-3 py-1 text-xs font-medium text-slate-200"
                    title={r.nombre}
                  >
                    {r.slug}
                  </span>
                ))
              ) : (
                <span className="rounded-full border border-white/10 bg-slate-800/60 px-3 py-1 text-xs text-slate-400">
                  sin-rol
                </span>
              )}
            </div>

            <div className="flex items-center gap-2">
              <div
                className="grid h-9 w-9 place-items-center rounded-xl border border-white/10 bg-slate-800/60 text-sm font-bold text-slate-200"
                title={`${authUser?.nombres ?? ""} ${authUser?.apellidos ?? ""}`}
              >
                {initials || "U"}
              </div>
              <button
                onClick={() => void logout()}
                className="rounded-xl bg-rose-500 px-3 py-2 text-xs font-semibold text-white shadow hover:bg-rose-400"
                title="Cerrar sesión"
              >
                Cerrar sesión
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Contenido */}
      <main className="mx-auto max-w-6xl px-4 py-8 md:px-6 md:py-10">
        <section className="mb-6">
          <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-slate-900/60 p-6 backdrop-blur-xl shadow-2xl">
            <div className="pointer-events-none absolute -inset-1 -z-10 rounded-3xl bg-gradient-to-tr from-cyan-400 to-indigo-500 opacity-20 blur-2xl" />
            <h2 className="text-xl font-extrabold tracking-tight text-white md:text-2xl">
              Bienvenido(a), {nombre}
            </h2>
            <p className="mt-1 text-sm text-slate-300">
              Accede a tus módulos según tu rol institucional.
            </p>
          </div>
        </section>

        <section className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {puedeAdmin && (
            <Link
              to="/admin"
              className="group relative overflow-hidden rounded-3xl border border-white/10 bg-slate-900/70 p-5 shadow-2xl outline-none transition hover:border-cyan-400/40 hover:bg-slate-900/80"
            >
              <div className="pointer-events-none absolute -inset-1 -z-10 rounded-3xl bg-gradient-to-br from-cyan-400/20 to-indigo-500/20 opacity-50 blur-2xl transition group-hover:opacity-80" />
              <div className="mb-3 grid h-12 w-12 place-items-center rounded-2xl bg-cyan-500/20">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M3 13h18M7 6h10M7 18h10" stroke="#22d3ee" strokeWidth="1.6" strokeLinecap="round" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-white">Administración</h3>
              <p className="mt-1 text-sm text-slate-300">
                Gestión de usuarios, roles y configuración del sistema.
              </p>
              <span className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-cyan-300">
                Entrar
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  className="translate-x-0 transition group-hover:translate-x-0.5"
                >
                  <path d="M5 12h14M13 5l7 7-7 7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                </svg>
              </span>
            </Link>
          )}

          {puedeEval && (
            <Link
              to={evaluadorPath}
              className="group relative overflow-hidden rounded-3xl border border-white/10 bg-slate-900/70 p-5 shadow-2xl transition hover:border-cyan-400/40 hover:bg-slate-900/80"
            >
              <div className="pointer-events-none absolute -inset-1 -z-10 rounded-3xl bg-gradient-to-br from-cyan-400/20 to-indigo-500/20 opacity-50 blur-2xl transition group-hover:opacity-80" />
              <div className="mb-3 grid h-12 w-12 place-items-center rounded-2xl bg-cyan-500/20">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M9 17l-3-3 1.5-1.5L9 14l7.5-7.5L18 8" stroke="#22d3ee" strokeWidth="1.6" strokeLinecap="round" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-white">Evaluaciones</h3>
              <p className="mt-1 text-sm text-slate-300">
                Registro, seguimiento y clasificación de evaluaciones.
              </p>
              <span className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-cyan-300">
                Entrar
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  className="translate-x-0 transition group-hover:translate-x-0.5"
                >
                  <path d="M5 12h14M13 5l7 7-7 7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                </svg>
              </span>
            </Link>
          )}

          {puedeCom && (
            <Link
              to="/comunicaciones"
              className="group relative overflow-hidden rounded-3xl border border-white/10 bg-slate-900/70 p-5 shadow-2xl transition hover:border-cyan-400/40 hover:bg-slate-900/80"
            >
              <div className="pointer-events-none absolute -inset-1 -z-10 rounded-3xl bg-gradient-to-br from-cyan-400/20 to-indigo-500/20 opacity-50 blur-2xl transition group-hover:opacity-80" />
              <div className="mb-3 grid h-12 w-12 place-items-center rounded-2xl bg-cyan-500/20">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M4 5h16v10H5.5L4 17.5V5z" stroke="#22d3ee" strokeWidth="1.6" strokeLinecap="round" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-white">Comunicaciones</h3>
              <p className="mt-1 text-sm text-slate-300">
                Publicaciones, anuncios y gestión de contenidos.
              </p>
              <span className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-cyan-300">
                Entrar
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  className="translate-x-0 transition group-hover:translate-x-0.5"
                >
                  <path d="M5 12h14M13 5l7 7-7 7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                </svg>
              </span>
            </Link>
          )}
        </section>

        <section className="mt-6">
          <div className="flex flex-col items-stretch gap-3 md:flex-row md:items-center">
            <Link
              to="/dashboard"
              className="inline-flex items-center justify-center rounded-xl border border-white/10 bg-slate-900/70 px-4 py-2 text-sm font-semibold text-slate-200 shadow hover:bg-slate-900/80"
            >
              Ir al panel principal
            </Link>
            <button
              onClick={() => void logout()}
              className="inline-flex items-center justify-center rounded-xl bg-rose-500 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-rose-400"
            >
              Cerrar sesión
            </button>
          </div>
        </section>
      </main>
    </div>
  );
}
